import type { ReactNode } from "react";
import { Link } from "react-router";

export function Crumbs({ items }: { items: Array<{ label: string; to?: string }> }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[13px] text-muted">
      {items.map((item, i) => (
        <span key={`${item.label}-${i}`} className="flex items-center gap-1.5">
          {i > 0 && <span aria-hidden>/</span>}
          {item.to ? (
            <Link className="link" to={item.to}>
              {item.label}
            </Link>
          ) : (
            <span>{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  badges,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  badges?: ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <h1 className="text-[22px] leading-tight font-semibold tracking-[-0.01em]">{title}</h1>
          {badges}
        </div>
        {subtitle && <div className="text-[13px] text-muted">{subtitle}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function MetricCard({
  label,
  value,
  lines = [],
  emphasis = false,
}: {
  label: string;
  value: string;
  lines?: ReactNode[];
  emphasis?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-1 rounded-[9px] border px-3.5 py-3 ${
        emphasis ? "border-subtle bg-subtle" : "border-line bg-surface"
      }`}
    >
      <span className="text-xs font-semibold text-muted">{label}</span>
      <span className="font-mono text-2xl leading-tight font-medium tracking-[-0.02em]">{value}</span>
      {lines.map((line, i) => (
        <span key={i} className="text-[11px] text-muted">
          {line}
        </span>
      ))}
    </div>
  );
}

/** A thin progress bar in the "live" blue. */
export function Progress({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      className="h-1.5 overflow-hidden rounded-full bg-track"
    >
      <div className="h-1.5 bg-link" style={{ width: `${pct}%` }} />
    </div>
  );
}
